import React from "react";
import { theme } from "../../theme";
import NotificationItem from "./NotificationItem";

export default function NotificationPanel({
  open,
  notifications,
  unreadCount,
  onClose,
  onOpenNotification,
  onMarkRead,
  onMarkAllRead,
  onRemove,
  onClearAll
}) {
  if (!open) return null;

  return (
    <>
      <button
        type="button"
        aria-label="Close notifications"
        onClick={onClose}
        style={styles.backdrop}
      />

      <aside style={styles.panel} aria-label="Notification Center">
        <header style={styles.header}>
          <div style={styles.headerIdentity}>
            <span style={styles.brandLogoFrame} aria-hidden="true">
              <img
                src="/logo/ecovanta-logo.png"
                alt=""
                style={styles.brandLogo}
              />
            </span>
            <div>
            <h2 style={styles.title}>Notifications</h2>
            <p style={styles.subtitle}>
              {unreadCount > 0
                ? `${unreadCount} unread update${unreadCount === 1 ? "" : "s"}`
                : "You are all caught up"}
            </p>
            </div>
          </div>

          <button type="button" onClick={onClose} style={styles.closeButton}>
            ×
          </button>
        </header>

        <div style={styles.toolbar}>
          <button
            type="button"
            onClick={onMarkAllRead}
            disabled={unreadCount === 0}
            style={{
              ...styles.toolbarButton,
              ...(unreadCount === 0 ? styles.disabledButton : {})
            }}
          >
            Mark all read
          </button>

          <button
            type="button"
            onClick={onClearAll}
            disabled={notifications.length === 0}
            style={{
              ...styles.toolbarButton,
              color: theme.colors.danger,
              ...(notifications.length === 0 ? styles.disabledButton : {})
            }}
          >
            Clear all
          </button>
        </div>

        <div style={styles.list}>
          {notifications.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>🔔</div>
              <strong style={styles.emptyTitle}>No notifications</strong>
              <p style={styles.emptyText}>
                Evidence, reports, document processing, CRM, and billing updates
                will appear here.
              </p>
            </div>
          ) : (
            notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onOpen={onOpenNotification}
                onMarkRead={onMarkRead}
                onRemove={onRemove}
              />
            ))
          )}
        </div>
      </aside>
    </>
  );
}

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 999,
    border: "none",
    background: "rgba(15, 23, 42, 0.24)",
    cursor: "default"
  },
  panel: {
    position: "fixed",
    top: 0,
    right: 0,
    zIndex: 1000,
    width: "min(420px, 94vw)",
    height: "100vh",
    display: "grid",
    gridTemplateRows: "auto auto 1fr",
    background: theme.colors.card,
    borderLeft: `1px solid ${theme.colors.border}`,
    boxShadow: "-18px 0 45px rgba(15, 23, 42, 0.16)"
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "16px",
    padding: "22px 20px 16px",
    borderBottom: `1px solid ${theme.colors.border}`
  },
  headerIdentity: {
    display: "flex",
    alignItems: "center",
    gap: "11px",
    minWidth: 0
  },
  brandLogoFrame: {
    width: "38px",
    height: "38px",
    display: "grid",
    placeItems: "center",
    overflow: "hidden",
    borderRadius: "10px",
    background: "#ffffff",
    flexShrink: 0
  },
  brandLogo: {
    width: "58px",
    height: "58px",
    maxWidth: "none",
    objectFit: "contain",
    display: "block"
  },
  title: {
    margin: 0,
    color: theme.colors.text,
    fontSize: "20px",
    fontWeight: 900
  },
  subtitle: {
    margin: "5px 0 0",
    color: theme.colors.muted,
    fontSize: "12px"
  },
  closeButton: {
    width: "34px",
    height: "34px",
    display: "grid",
    placeItems: "center",
    borderRadius: theme.radius.pill,
    border: `1px solid ${theme.colors.border}`,
    background: "#ffffff",
    color: theme.colors.text,
    fontSize: "22px",
    lineHeight: 1,
    cursor: "pointer"
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    padding: "10px 16px",
    borderBottom: `1px solid ${theme.colors.border}`,
    background: "#f8fafc"
  },
  toolbarButton: {
    border: "none",
    background: "transparent",
    color: theme.colors.primary,
    fontSize: "12px",
    fontWeight: 800,
    cursor: "pointer"
  },
  disabledButton: {
    opacity: 0.45,
    cursor: "not-allowed"
  },
  list: {
    overflowY: "auto"
  },
  emptyState: {
    minHeight: "360px",
    padding: "48px 30px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center"
  },
  emptyIcon: {
    width: "58px",
    height: "58px",
    display: "grid",
    placeItems: "center",
    borderRadius: "18px",
    background: "#ecfdf5",
    fontSize: "25px",
    marginBottom: "16px"
  },
  emptyTitle: {
    color: theme.colors.text,
    fontSize: "16px"
  },
  emptyText: {
    maxWidth: "290px",
    color: theme.colors.muted,
    lineHeight: 1.6,
    fontSize: "13px"
  }
};
